import { openDB } from "idb";

// Local-first cache for scoreadmin-fe (card #1662). Currently a single store:
// the global test catalog (GET /catalog, score-api's score-catalog table,
// docs/score/catalog-audit-findings.md section 9a). Same shape as
// scribe-fe's local/db.js and announcer-fe's local/db.js (the established
// idb pattern across the score surface): a STORES map of { keyPath, indexes? }
// per object store, versioned via a single DB_VERSION bump, a thin
// per-entity repository built on generic get/getAll/bulkPut helpers.
//
// catalogDefs is deliberately a SEPARATE store from the score-test rows
// scribe-fe/announcer-fe cache under the name `tests` (GET /test,
// reconcile-enriched, per-show, org-scoped -- scoring already depends on
// that today). The catalog is a different, unrelated thing: the raw global
// test DEFINITIONS (~148 rows, no orgId, rarely changes). Conflating the two
// stores would break scoring.

export const DB_NAME = "scoreadmin-offline";
export const DB_VERSION = 1;

export const STORES = {
  // card #1662 — GET /catalog, keyed by the def's stable slug id. Global,
  // not org-scoped: no showId/orgId index needed.
  catalogDefs: { keyPath: "id" },
};

let dbPromise;

export function openDb() {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, tx) {
        for (const [name, cfg] of Object.entries(STORES)) {
          const store = db.objectStoreNames.contains(name)
            ? tx.objectStore(name)
            : db.createObjectStore(name, { keyPath: cfg.keyPath });
          for (const [idxName, keyPath] of Object.entries(cfg.indexes ?? {})) {
            if (!store.indexNames.contains(idxName)) {
              store.createIndex(idxName, keyPath);
            }
          }
        }
      },
    });
  }
  return dbPromise;
}

// ── Generic repository ────────────────────────────────────────────────────

export async function get(store, key) {
  return (await openDb()).get(store, key);
}

export async function getAll(store) {
  return (await openDb()).getAll(store);
}

export async function bulkPut(store, values) {
  if (!values?.length) return;
  const db = await openDb();
  const tx = db.transaction(store, "readwrite");
  for (const v of values) tx.store.put(v);
  await tx.done;
}

// ── Per-entity accessors ─────────────────────────────────────────────────

// card #1662 — the global test catalog. Hydration policy: fetch once at
// app-init if the store is empty, otherwise trust the cache (see
// hooks/useCatalogDefs.js) -- unlike classes/rides, no per-show or
// per-scribe scoping applies here. A GET /catalog response that comes back
// genuinely empty (`[]`) simply leaves the store empty, so the NEXT app-init
// re-fetches too (bulkPut no-ops on an empty array): deliberately no sentinel
// pinning a transient empty response as "hydrated forever" (#1662 judge
// follow-up round 3) -- an empty catalog is a cheap, self-correcting
// ~148-row global GET, not worth the extra store/transaction to avoid.
export const catalogDefs = {
  get: (id) => get("catalogDefs", id),
  getAll: () => getAll("catalogDefs"),
  putAll: (rows) => bulkPut("catalogDefs", rows),
};
